import { type Dictionary, type EntityState } from '@ngrx/entity';

import { type ElementsState } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementFrom,
  type CanvasElementId,
  type CanvasElementPath,
  type CanvasElementPathNode,
  type CanvasPathString,
  type ContainerVariantType,
  type ElementTargetPath,
  type NavigationElementType,
  type SlotId,
} from '@blaze-runtime/engine-domain';

import { hasSlots, isWrapper } from '../../canvas-elements/element-model/element-props-guards';
import {
  isElementType,
  isNavigationElement,
  isVariantOfContainer,
} from '../../canvas-elements/element-model/element-type-guards';
import { getHostSlotIdOfNavigationElement } from '../../canvas-elements/element-model/slots';

// TODO: test this file

/**
 * Returns if an element is a root of the layout.
 * This element has no parentNode defined.
 * When nested layouts are used this is the first entrypoint element of this layout.
 *
 * The element is root when it is not attached to any other element within the same namespace.
 * Most often this is the 'root' element of the page or any other namespace layout.
 * It can also be a 'dialog' element, which is stored in a separate layout.
 */
export function isRoot(
  element?: CanvasElement,
): element is CanvasElement & { parentNode: undefined } {
  return element !== undefined && element.parentNode === undefined;
}

export function isRootDialog(
  element?: CanvasElement,
): element is CanvasElementFrom<'dialog'> & { parentNode: undefined } {
  return isRoot(element) && element.type === 'dialog';
}

/**
 * Element/slot is an ancestor of the selected element/slot, but is not the selected element/slot itself
 */
export function isOnPath(path: CanvasElementPath, id?: CanvasElementId, slot?: SlotId): boolean {
  if (id === undefined) {
    return false;
  }

  return (
    !isSelected(path, id, slot) &&
    path.some((p) => p.id === id && (slot === undefined || p.slot === slot))
  );
}

/**
 * Returns whether the provided element (id) or slot (id + slot) is selected
 */
export function isSelected(path: CanvasElementPath, id: CanvasElementId, slot?: SlotId): boolean {
  const last = path[path.length - 1];

  return last !== undefined && last.id === id && last.slot === slot;
}

export function buildPath(
  elements: Dictionary<CanvasElement>,
  node?: CanvasElementPathNode,
): CanvasElementPath {
  if (!node) {
    return [];
  }

  const path: CanvasElementPath = [node];

  for (
    let element = elements[node.id];
    element?.parentNode;
    element = elements[element.parentNode?.id]
  ) {
    path.push(element.parentNode);
  }

  return path.reverse();
}

/**
 * Identifies placement of the element.
 *
 * @param elements the dictionary of elements
 * @param elementId the element to be found
 * @returns the path targeting the parent, slot and index at which the provided element is placed
 */
// TODO: test
export function getElementPath(
  elements: Dictionary<CanvasElement>,
  elementId: CanvasElementId | undefined,
): ElementTargetPath | undefined {
  if (!elementId) {
    return;
  }
  const element = elements[elementId];
  if (!element) {
    throw new Error('Element does not exist');
  }

  if (!element.parentNode || !element.parentNode.slot) {
    throw new Error('Parent node data error');
  }
  const parentElement = elements[element.parentNode.id];
  if (!parentElement || !hasSlots(parentElement)) {
    throw new Error('Parent element error');
  }

  const index = parentElement.slots[element.parentNode.slot]?.children.indexOf(elementId) ?? -1;
  if (index < 0) {
    throw new Error('Element could not be found in slot');
  }

  return {
    id: parentElement.id,
    parentId: parentElement.id,
    index,
    slot: element.parentNode.slot,
  };
}

/**
 * Maps {@link CanvasElementPath} to string
 * ```
 * <element_id>:<slot>;...;<element_id>:<slot>;<element_id>
 * ```
 *
 * Example result:
 * ```
 * 0:0;5:footer;6:2;13
 * ```
 */
export function mapPathToString(path: CanvasElementPath): CanvasPathString {
  return path
    .map((p) => {
      const hasSlot = p.slot !== undefined;
      return hasSlot ? `${p.id}:${p.slot}` : `${p.id}`;
    })
    .join(';');
}

/**
 * Root elements can be nested in another elements.
 * For example Nav Container has Root element inside it added during runtime.
 * Parent reference is hidden slot's hosting data
 * @param state
 * @param root
 * @param pageId - narrow down the search to a certain page
 */
export function findHostingOfRoot(
  state: EntityState<CanvasElement>,
  root: CanvasElement,
  pageId?: string,
): { elementId: CanvasElementId; slotId: SlotId } | undefined {
  for (const elementId of state.ids as CanvasElementId[]) {
    const element = state.entities[elementId];
    if (hasSlots(element)) {
      const slotHostingRoot = element.slots.ids.find(
        (slotId) => element.slots[slotId]?.hosting?.ns === root.ns,
      );

      const matchesPage = pageId ? element.ns === pageId : true;
      if (slotHostingRoot && matchesPage) {
        return { elementId, slotId: slotHostingRoot };
      }
    }
  }

  return;
}

export function findHostingOfPage(
  state: EntityState<CanvasElement>,
  pageId: string,
): CanvasElement | undefined {
  for (const elementId of state.ids as CanvasElementId[]) {
    const element = state.entities[elementId];
    if (
      hasSlots(element) &&
      element.slots.ids.some((slotId) => element.slots[slotId]?.hosting?.ns === pageId)
    ) {
      return element;
    }
  }

  return;
}

/** Finds wrapper element for provided element */
export function findWrapperForElement(
  state: Pick<ElementsState, 'entities'>,
  elementId: CanvasElementId,
): CanvasElementId | undefined {
  for (
    let element = state.entities[elementId];
    element?.parentNode;
    element = state.entities[element.parentNode.id]
  ) {
    if (isWrapper(element) && element.props.wrapper.mainElementId === elementId) {
      return element.id;
    }
  }

  return;
}

/**
 * Finds navigation hosting element of its child element
 * @param state
 * @param element
 * @param types
 * @param pageId
 */
export function findNavigationHostingOfElement(
  state: EntityState<CanvasElement>,
  element?: CanvasElement,
  types?: NavigationElementType[],
  pageId?: string,
): CanvasElement | undefined {
  if (!element) {
    return undefined;
  }

  if (isRoot(element)) {
    const hosting = findHostingOfRoot(state, element, pageId);
    if (hosting?.elementId) {
      const hostingElement = state.entities[hosting.elementId];
      const isNavElement = types?.length
        ? types.some((type) => isElementType(hostingElement, type))
        : isNavigationElement(hostingElement);

      return isNavElement ? hostingElement : undefined;
    }
    return undefined;
  }

  const parentElement = state.entities[element.parentNode?.id || ''];
  return findNavigationHostingOfElement(state, parentElement, types, pageId);
}

export function findPrimaryContainerForNavigationElement(
  state?: EntityState<CanvasElement>,
  element?: CanvasElement,
): CanvasElementFrom<ContainerVariantType> | undefined {
  if (!state || !isNavigationElement(element)) {
    return undefined;
  }

  const hostSlotId = getHostSlotIdOfNavigationElement(element);

  if (!hostSlotId) {
    console.warn(`Missing handler for element type: ${element.type}`);
    return undefined;
  }

  const hosting = element.slots[hostSlotId]?.hosting?.ns;
  if (!hosting) {
    return undefined;
  }

  const rootElementId = (state.ids as string[]).find((id) => {
    const el = state.entities[id];
    return isRoot(el) && el.ns === hosting;
  });

  const rootElement = state.entities[rootElementId || ''];
  if (!rootElement) {
    return undefined;
  }

  const firstContainerId = rootElement.slots?.main?.children.find((id) => {
    const el = state.entities[id];
    return isVariantOfContainer(el);
  });

  const foundElement = state.entities[firstContainerId || ''];

  return isVariantOfContainer(foundElement) ? foundElement : undefined;
}
